import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import BootScreen from '../components/BootScreen';
import useSymbolTrie from '../hooks/useSymbolTrie';
import SymbolSearchInput from '../components/SymbolSearchInput';
import { Sparklines, SparklinesLine } from 'react-sparklines';

const fmtDelta = (n) => n == null ? 'N/A' : `${n > 0 ? '+' : ''}${n.toFixed(2)}%`;
const getDeltaClass = (n) => n > 0 ? 'green' : n < 0 ? 'red' : '';

const Dashboard = () => {
    const [trackedStocks, setTrackedStocks] = useState([]);
    const [prices, setPrices] = useState({});
    const [newStock, setNewStock] = useState('');
    const [error, setError] = useState('');
    const [showBoot, setShowBoot] = useState(!localStorage.getItem('booted'));
    const navigate = useNavigate();

    useEffect(() => {
        if (trackedStocks.length > 0) {
            fetchPrices(trackedStocks);  // Fetch prices when tracked stocks are updated
        }
    }, [trackedStocks]);

    useEffect(() => {
        if (!showBoot) {
            fetchTrackedStocks(); // initial load

            // Polling every 10 seconds
            const interval = setInterval(() => {
                fetchTrackedStocks();
            }, 10000); // every 10 sec

            return () => clearInterval(interval); // cleanup
        }
    }, [showBoot]);


    const fetchTrackedStocks = async () => {
        try {
            const response = await api.get('/users/stocks');
            setTrackedStocks(response.data); // contains {ticker, added_at}
        } catch (err) {
            setError('Failed to load tracked stocks.');
        }
    };


    const fetchPrices = async (stockList) => {
        const updatedData = {};
        for (let { ticker, added_at } of stockList) {
            try {
                const response = await api.get(`/stocks/${ticker}/changes`, {
                    params: { added_at }
                });
                updatedData[ticker] = response.data;
            } catch (err) {
                console.error(`Failed to fetch changes for ${ticker}:`, err);
                updatedData[ticker] = { price: 'Error', error: true };
            }
        }
        setPrices(updatedData);
    };

    const fetchStockSummaries = async () => {
        try {
          const response = await api.get('/users/stocks/summary');
          const summary = response.data;
      
          // Store all objects now (ticker, price, performance, etc.)
          setTrackedStocks(summary);
        } catch (err) {
          console.error("Error fetching summary:", err);
          setError('Failed to load stock summary.');
        }
      };
      

    // inside component:
    const { searchPrefix } = useSymbolTrie();

    const handleAddStock = async (ticker) => {
        setError('');

        if (!searchPrefix(ticker)) {
            setError('Unknown ticker.');
            return;
        }

        try {
            await api.post('/users/stocks', { ticker });
            setTrackedStocks(prev => [...prev, ticker]);
            fetchPrices([...trackedStocks, ticker]);
            setNewStock('');
        } catch (err) {
            console.error('Add failed:', err);
            setError('Failed to add stock.');
        }
    };

    const handleRemoveStock = async (ticker) => {
        try {
            await api.delete('/users/stocks', { data: { ticker } });
            const newList = trackedStocks.filter(stock => stock !== ticker);
            setTrackedStocks(newList);
            fetchPrices(newList);

        } catch (err) {
            setError('Failed to remove stock.');
        }
    };

    if (showBoot) {
        return (
            <BootScreen onFinish={() => {
                localStorage.setItem('booted', 'true');
                setShowBoot(false);
            }} />
        );
    }

    return (
        <div className="dashboard-container">
            <div className="stock-list-panel">
                <div className="dashboard-header">
                    <h2>Stock Dashboard</h2>
                    <SymbolSearchInput onAdd={handleAddStock} />

                </div>

                <ul>
                    {trackedStocks.length > 0 ? trackedStocks.map(({ ticker, added_at }) => (
                        <li key={ticker} className="stock-tile">
                            <span className="ticker" title={`Added: ${new Date(added_at).toLocaleDateString()}`}>{ticker}</span>
                            <span className={`price ${getDeltaClass(prices[ticker]?.change_1d)}`}>
                                {prices[ticker]?.price ?? '...'}
                            </span>
                            <div className={`change-metrics ${getDeltaClass(prices[ticker]?.change_1w)}`}>1W {fmtDelta(prices[ticker]?.change_1w)}</div>
                            <div className={`change-metrics ${getDeltaClass(prices[ticker]?.change_1m)}`}>1M {fmtDelta(prices[ticker]?.change_1m)}</div>
                            <div className={`change-metrics ${getDeltaClass(prices[ticker]?.performance_change)}`}>
                                Since Added: {fmtDelta(prices[ticker]?.performance_change)}
                            </div>
                            <div className="mini-chart">
                                {Array.isArray(prices[ticker]?.history) ? (
                                    <Sparklines data={prices[ticker].history}>
                                        <SparklinesLine color="#00e676" style={{ fill: "none" }} />
                                    </Sparklines>
                                ) : <span style={{ color: "#888" }}>N/A</span>}
                            </div>
                            <button onClick={() => handleRemoveStock(ticker)}>×</button>
                        </li>
                    ))
                        : <p>No stocks tracked yet.</p>}
                </ul>
            </div>

            <div className="future-panel">
                <h3>Future Panel</h3>
                <button onClick={() => {
                    localStorage.removeItem('booted');
                    window.location.reload();
                }}>
                    Reboot Findows 2000™
                </button>
                <button
                    onClick={() => {
                        alert("💸 SUCCESS: More money downloaded.\n\nThe Fed has been notified.");
                        console.log("🤑 User has downloaded more money. This is fine.");
                    }}
                    style={{
                        marginTop: "10px",
                        backgroundColor: "#00c853",
                        color: "#fff",
                        fontWeight: "bold"
                    }}
                >
                    💾 Download More Money
                </button>

            </div>
        </div>



    );
};

export default Dashboard;
